import React, { useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BdsLabelPill from '../../../../shared/components/v3/BdsLabelPill/BdsLabelPill';
import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import GenericCard from '../../../../shared/components/v3/Cards/GenericCard/GenericCard';
import ExpansiveContainer from '../../../../shared/components/v3/ExpansiveContainer/ExpansiveContainer';
import { BENEFIT_PROVIDER_STATUS_DATA } from '../../../../shared/constants/benefits';
import { NORTH_EAST_ARROW } from '../../../../shared/constants/images';
import { CLICK_ACTION, COLLABORATOR_BENEFIT_DETAIL_STATUS_DETAIL } from '../../../../shared/constants/tagManager';
import { ICollaboratorBenefit, IMembersData } from '../../../../shared/interfaces/businessData';
import type { BenefitsState } from '../../../../shared/interfaces/collaborators';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';
import BenefitDetailModal from '../BenefitDetailModal/BenefitDetailModal';
import DependentsCard from './DependentsCard/DependentsCard';

import './HealthProtection.scss';

interface IHealthProtectionProps {
  healthData: BenefitsState;
  collaborator: IMembersData;
  supportDependents: boolean;
}

const HealthProtection = ({ healthData, collaborator, supportDependents }: IHealthProtectionProps) => {
  const [isBenefitDetailOpen, setIsBenefitDetailOpen] = useState<boolean>(false);
  const [benefit, setBenefit] = useState<ICollaboratorBenefit>();

  const { t } = useTranslation();

  const healthBenefits = useMemo(() => healthData.data?.['health'] || [], [healthData.data]);
  const healthBenefitsCount = useMemo(() => healthBenefits.length, [healthBenefits]);

  const onClickBenefit = (benefit: ICollaboratorBenefit) => {
    TagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_BENEFIT_DETAIL_STATUS_DETAIL);
    setBenefit(benefit);
    setIsBenefitDetailOpen(true);
  };

  const renderSkeleton = () => (
    <div className="hp-skeleton-container mt-8">
      <Skeleton className="hp-skeleton" variant="rectangular" />
    </div>
  );

  const renderBenefitCard = (benefit: ICollaboratorBenefit) => {
    const isValidStatus = (status: string): status is keyof typeof BENEFIT_PROVIDER_STATUS_DATA =>
      status in BENEFIT_PROVIDER_STATUS_DATA;

    const validStatus = isValidStatus(benefit.status) ? benefit.status : null;

    const statusIcon = validStatus ? BENEFIT_PROVIDER_STATUS_DATA[validStatus].icon : undefined;
    const tagVariant = validStatus ? BENEFIT_PROVIDER_STATUS_DATA[validStatus].tagVariant : 'info';

    return (
      <GenericCard
        key={benefit.benefitId}
        title={benefit.title}
        description={benefit.providerName}
        icon={benefit.icon}
        action={() => onClickBenefit(benefit)}
        iconButton={NORTH_EAST_ARROW}
        size="l"
        customTag={
          <BdsLabelPill text={t(benefit.statusKey).toUpperCase()} iconLeft={statusIcon} variant={tagVariant} />
        }
      />
    );
  };

  if (healthData.error) {
    return (
      <div className="hp-error-container p-8">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
        />
      </div>
    );
  }

  return (
    <>
      {healthData.isLoading ? (
        renderSkeleton()
      ) : (
        <div
          className="hp-benefits-container flex flex-col p-8 gap-8"
          data-experience="new"
          data-testid="benefits-section"
        >
          <div className="hp-benefits-header flex flex-row gap-4 items-baseline">
            <div className="title">{t(TRANSLATIONS_GLOBAL.available)}</div>
            <span className="availables">
              {t(TRANSLATIONS_GLOBAL.totalHealthBenefits, {
                count: healthBenefitsCount,
                benefits_number: healthBenefitsCount
              })}
            </span>
          </div>
          <ExpansiveContainer>{healthBenefits.map((benefit) => renderBenefitCard(benefit))}</ExpansiveContainer>
          {benefit && (
            <BenefitDetailModal
              isOpen={isBenefitDetailOpen}
              onClose={() => setIsBenefitDetailOpen(false)}
              collaborator={collaborator}
              benefit={benefit}
            />
          )}
        </div>
      )}
      {supportDependents && <DependentsCard collaborator={collaborator} />}
    </>
  );
};

export default HealthProtection;
